// SPDX-License-Identifier: GPL-3.0-or-later
// Music data: chords, rhythm strings, arpeggio shapes, layers and stinger phrases.
// All patterns are original. This file has no logic that touches audio.
//
// A rhythm string has 16 characters, one for each sixteenth note of a bar.
//   "." rest, "x" hit, "o" accent (or open hat), "g" ghost (soft hit).
// A bass string uses "r" root, "o" octave, "f" fifth and "." rest.
// An arpeggio shape has 16 numbers. -1 is a rest. A number n picks chord tone n % 3 and
// goes up n / 3 octaves.

/** The states of the music. "off" fades out and stops. */
export type MusicState = "attract" | "play" | "multiball" | "featured" | "off";

/** A state that plays. */
export type ActiveMusicState = Exclude<MusicState, "off">;

/** Waves that the lead voice can use. */
export type LeadWave = "square" | "sawtooth" | "triangle" | "sine";

/** A triad: the root as a MIDI number, and the kind of third. */
export interface Chord {
  readonly root: number;
  readonly minor: boolean;
}

/** One section of 4 or 8 bars. One chord for each bar. */
export interface Section {
  readonly id: string;
  readonly chords: readonly Chord[];
  readonly kick: string;
  readonly snare: string;
  readonly hat: string;
  readonly bass: string;
  readonly arp: readonly number[];
}

/** Which layers play in a state, and how loud. */
export interface Layers {
  readonly kick: boolean;
  readonly snare: boolean;
  readonly hat: boolean;
  readonly bass: boolean;
  readonly pad: boolean;
  readonly arp: boolean;
  /** Play the arpeggio on every Nth sixteenth. */
  readonly arpEvery: 1 | 2;
  readonly arpGain: number;
  readonly arpWave: LeadWave;
  /** The extra layer of multiball: a quiet arpeggio one octave up on the odd steps. */
  readonly sparkle: boolean;
  /** The triumphant layer of a featured turn: long lead notes on the bar. */
  readonly fanfare: boolean;
  /** Scale for every note velocity, 0..1. */
  readonly level: number;
}

/** All data of one state. */
export interface Arrangement {
  readonly bpm: number;
  readonly layers: Layers;
  readonly sections: readonly Section[];
}

/** A short phrase for a stinger. */
export interface Phrase {
  readonly notes: readonly number[];
  readonly stepS: number;
  readonly wave: LeadWave;
  readonly vel: number;
}

/** Sixteenth notes in a bar. */
export const STEPS_PER_BAR = 16;

const min = (root: number): Chord => ({ root, minor: true });
const maj = (root: number): Chord => ({ root, minor: false });

// Bass roots: A2 45, C3 48, D3 50, E2 40, F2 41, G2 43.
const A = 45;
const C = 48;
const D = 50;
const E = 40;
const F = 41;
const G = 43;

const UP_DOWN = [0, 1, 2, 3, 2, 1, 0, 1, 2, 3, 4, 3, 2, 1, 2, 1] as const;
const CLIMB = [0, -1, 1, -1, 2, -1, 3, -1, 2, -1, 4, -1, 3, -1, 2, 1] as const;
const BOUNCE = [0, 2, 1, 2, 0, 2, 1, 4, 3, 5, 4, 3, 2, 3, 1, 2] as const;
const SLOW = [0, -1, -1, -1, 1, -1, -1, -1, 2, -1, -1, -1, 1, -1, -1, -1] as const;

const ATTRACT: Arrangement = {
  bpm: 112,
  layers: {
    kick: false,
    snare: false,
    hat: false,
    bass: false,
    pad: true,
    arp: true,
    arpEvery: 2,
    arpGain: 0.55,
    arpWave: "triangle",
    sparkle: false,
    fanfare: false,
    level: 0.7,
  },
  sections: [
    {
      id: "attract-a",
      chords: [min(A), maj(F), maj(C), maj(G)],
      kick: "................",
      snare: "................",
      hat: "................",
      bass: "................",
      arp: SLOW,
    },
    {
      id: "attract-b",
      chords: [maj(F), maj(C), maj(G), min(A)],
      kick: "................",
      snare: "................",
      hat: "................",
      bass: "................",
      arp: UP_DOWN,
    },
  ],
};

const PLAY: Arrangement = {
  bpm: 120,
  layers: {
    kick: true,
    snare: true,
    hat: true,
    bass: true,
    pad: true,
    arp: true,
    arpEvery: 1,
    arpGain: 0.8,
    arpWave: "square",
    sparkle: false,
    fanfare: false,
    level: 0.85,
  },
  sections: [
    {
      id: "play-verse",
      chords: [min(A), maj(F), maj(C), maj(G)],
      kick: "x.......x.x.....",
      snare: "....x.......x..g",
      hat: "..x...x...x...x.",
      bass: "r..rr.r.r..rr.o.",
      arp: UP_DOWN,
    },
    {
      id: "play-chorus",
      chords: [maj(F), maj(G), min(A), min(A), maj(F), maj(G), maj(C), maj(E)],
      kick: "x...x...x...x...",
      snare: "....x.......x...",
      hat: "x.x.x.x.x.x.x.xo",
      bass: "r.r.r.r.r.r.r.f.",
      arp: BOUNCE,
    },
    {
      id: "play-bridge",
      chords: [min(D), min(A), maj(F), maj(E)],
      kick: "x.....x.x.....x.",
      snare: "....x.......x.g.",
      hat: "..x...x...x...x.",
      bass: "r...r...r.o.r...",
      arp: CLIMB,
    },
  ],
};

const MULTIBALL: Arrangement = {
  bpm: 126,
  layers: {
    kick: true,
    snare: true,
    hat: true,
    bass: true,
    pad: true,
    arp: true,
    arpEvery: 1,
    arpGain: 0.9,
    arpWave: "square",
    sparkle: true,
    fanfare: false,
    level: 1,
  },
  sections: [
    {
      id: "multi-drive",
      chords: [min(A), min(A), maj(F), maj(G)],
      kick: "x..xx..xx..xx.x.",
      snare: "....x.g.....x.g.",
      hat: "xxxxxxxxxxxxxxxo",
      bass: "rrrrrrrrrrrrrrrr",
      arp: BOUNCE,
    },
    {
      id: "multi-rush",
      chords: [min(A), maj(F), maj(C), maj(G), min(A), maj(F), maj(G), maj(E)],
      kick: "x...x...x...x..x",
      snare: "....x...x...x.xx",
      hat: "xxxoxxxoxxxoxxxo",
      bass: "r.rrr.rrr.rrr.rf",
      arp: UP_DOWN,
    },
  ],
};

const FEATURED: Arrangement = {
  bpm: 124,
  layers: {
    kick: true,
    snare: true,
    hat: true,
    bass: true,
    pad: true,
    arp: true,
    arpEvery: 1,
    arpGain: 0.8,
    arpWave: "square",
    sparkle: false,
    fanfare: true,
    level: 0.95,
  },
  sections: [
    {
      id: "featured-hero",
      chords: [maj(C), maj(G), min(A), maj(F)],
      kick: "x...x...x...x...",
      snare: "....x.......x...",
      hat: "x.x.x.x.x.x.x.xo",
      bass: "r.r.o.r.r.r.o.f.",
      arp: UP_DOWN,
    },
    {
      id: "featured-lift",
      chords: [maj(F), maj(G), maj(C), maj(C), maj(F), maj(G), maj(C), maj(G)],
      kick: "x..xx...x..xx...",
      snare: "....x.......x.xx",
      hat: "..x...x...x...xo",
      bass: "r..rr.r.r..rr.f.",
      arp: BOUNCE,
    },
  ],
};

const ARRANGEMENTS: Readonly<Record<ActiveMusicState, Arrangement>> = {
  attract: ATTRACT,
  play: PLAY,
  multiball: MULTIBALL,
  featured: FEATURED,
};

/** The data of a state that plays. */
export function arrangementFor(state: ActiveMusicState): Arrangement {
  return ARRANGEMENTS[state];
}

/** Seconds in one sixteenth note at a tempo. */
export function stepSeconds(bpm: number): number {
  return 60 / bpm / 4;
}

/** The frequency of a MIDI note in Hz. */
export function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/** The MIDI note of chord tone `index` (0..5) as an arpeggio note. */
export function arpNote(chord: Chord, index: number): number {
  const third = chord.minor ? 3 : 4;
  const tones = [0, third, 7];
  const tone = tones[index % 3] ?? 0;
  return chord.root + 24 + tone + 12 * Math.floor(index / 3);
}

/** A falling phrase for a lost ball. */
export const DRAIN_PHRASE: Phrase = {
  notes: [69, 64, 60, 57],
  stepS: 0.13,
  wave: "triangle",
  vel: 0.9,
};

/** A rising phrase for a jackpot. */
export const JACKPOT_PHRASE: Phrase = {
  notes: [60, 64, 67, 72, 76, 79, 84],
  stepS: 0.07,
  wave: "square",
  vel: 1,
};

/** The end phrase of a game. */
export const GAME_OVER_PHRASE: Phrase = {
  notes: [72, 69, 65, 64, 60, 57, 52],
  stepS: 0.2,
  wave: "triangle",
  vel: 0.9,
};
